import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  role: string
  is_active: boolean
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('auth_token')
    const userData = localStorage.getItem('user')

    if (!token) {
      setLoading(false)
      return
    }

    // Optimistically populate from cache so the UI doesn't flicker
    if (userData) {
      try {
        setUser(JSON.parse(userData))
      } catch {
        localStorage.removeItem('user')
      }
    }

    // Always refresh from the backend so a role change (e.g. admin demoted
    // to analyst) takes effect on next page load without requiring re-login.
    api
      .get('/auth/profile')
      .then((resp) => {
        const fresh = resp.data as User
        setUser(fresh)
        localStorage.setItem('user', JSON.stringify(fresh))
      })
      .catch((err) => {
        // 401 handler in api.ts already clears auth + redirects to /login.
        // For any other failure keep the cached user so the app stays usable.
        if (err?.response?.status === 401) {
          setUser(null)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  const logout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    setUser(null)
    navigate('/login')
    toast.success('Logged out successfully')
  }

  const setAuthData = (token: string, userData: User) => {
    localStorage.setItem('auth_token', token)
    localStorage.setItem('user', JSON.stringify(userData))
    setUser(userData)
  }

  const isAdmin = user?.role === 'admin'
  // Any authenticated non-admin (analyst / legacy manager / user / viewer) is
  // treated as a read-only analyst by the frontend.
  const isAnalyst = !!user && !isAdmin
  // `isManager` is retained as an alias of `isAdmin` so existing callers keep
  // working. New code should use `isAdmin` / `canExport` / `canMutate` instead.
  const isManager = isAdmin

  const canExport = isAdmin
  const canMutate = isAdmin
  const canSeeMonitoring = isAdmin
  const canSeeAudit = isAdmin

  return {
    user,
    loading,
    logout,
    setAuthData,
    isAdmin,
    isAnalyst,
    isManager,
    canExport,
    canMutate,
    canSeeMonitoring,
    canSeeAudit,
    isAuthenticated: !!user,
  }
}
